import type { Authority } from "../auth/authorities";
import type { ProductId } from "./registry";

/**
 * Every route the console may call, copied from the two documents that are
 * the only source of truth for it: docs/products/labeeb.md and
 * docs/products/mcp.md.
 *
 * `routes.docs.test.ts` parses both documents' `### \`METHOD /admin/…\``
 * headings and their "Route to authority" tables and asserts this list equals
 * them, so a route documented and not listed here fails, as does one listed
 * here after its heading is gone. The harness's request guard
 * (`test/requestGuard.ts`) then fails any page test whose components request
 * a path that matches nothing here, or matches a retired route.
 *
 * Paths are the console's own paths, product prefix included, with the
 * parameter names the documents' headings use.
 */

export type RouteMethod = "GET" | "POST" | "PATCH" | "DELETE";

export type RouteAuthority =
  /** Liveness. No token is checked. */
  | { kind: "public" }
  /**
   * One authority. `force` is the extra one the same route requires when it
   * is called with `?force=true`: `quota:write` alone must be refused then.
   */
  | { kind: "single"; authority: Authority; force?: Authority }
  /**
   * MCP's `POST /admin/tenant/{id}` takes two actions on one route; each body
   * field present requires its own authority, and both present require both.
   */
  | { kind: "body"; fields: Record<string, Authority> }
  /**
   * Still a heading in the doc, still mounted by the gateway, no longer served
   * by the product. The console must never call it.
   */
  | { kind: "retired" };

export interface CatalogRoute {
  product: ProductId;
  method: RouteMethod;
  path: string;
  authority: RouteAuthority;
}

const one = (authority: Authority): RouteAuthority => ({ kind: "single", authority });

function route(
  product: ProductId,
  method: RouteMethod,
  path: string,
  authority: RouteAuthority,
): CatalogRoute {
  return { product, method, path: `/${product}${path}`, authority };
}

const labeeb = (method: RouteMethod, path: string, authority: RouteAuthority) =>
  route("labeeb", method, path, authority);
const mcp = (method: RouteMethod, path: string, authority: RouteAuthority) =>
  route("mcp", method, path, authority);

export const ROUTE_CATALOG: readonly CatalogRoute[] = [
  // ---------------------------------------------------------------- labeeb --
  labeeb("GET", "/admin/health", { kind: "public" }),
  labeeb("GET", "/admin/tenants", one("fleet:read")),
  labeeb("GET", "/admin/catalog", one("settings:read")),
  labeeb("GET", "/admin/config", one("settings:read")),
  labeeb("GET", "/admin/tenant/{tenant_id}", one("tenant:read")),
  labeeb("GET", "/admin/tenant/{tenant_id}/history", one("audit:read")),
  labeeb("GET", "/admin/tenant/{tenant_id}/usage", one("tenant:read")),
  labeeb("GET", "/admin/tenant/{tenant_id}/feedback", one("tenant:read")),
  labeeb("GET", "/admin/tenant/{tenant_id}/users", one("tenant:read")),
  labeeb("GET", "/admin/tenant/{tenant_id}/standing", one("tenant:read")),
  labeeb("PATCH", "/admin/tenant/{tenant_id}/standing", one("block:write")),
  labeeb("PATCH", "/admin/tenant/{tenant_id}/users/{user_id}/standing", one("block:write")),
  labeeb("GET", "/admin/tenant/{tenant_id}/settings", one("settings:read")),
  labeeb("PATCH", "/admin/tenant/{tenant_id}/settings", one("settings:write")),
  labeeb("GET", "/admin/environment/settings", one("settings:read")),
  labeeb("PATCH", "/admin/environment/settings", one("settings:write")),
  labeeb("GET", "/admin/tenant/{tenant_id}/rules", one("settings:read")),
  labeeb("GET", "/admin/tenant/{tenant_id}/rules/{rule_id}", one("settings:read")),
  labeeb("PATCH", "/admin/tenant/{tenant_id}/rules/{rule_id}", one("settings:write")),
  labeeb("GET", "/admin/tiers", one("quota:read")),
  labeeb("GET", "/admin/tiers/{tier_id}", one("quota:read")),
  labeeb("PATCH", "/admin/tiers/{tier_id}", one("quota:write")),
  labeeb("GET", "/admin/tenant/{tenant_id}/quota", one("tenant:read")),
  labeeb("PATCH", "/admin/tenant/{tenant_id}/quota", one("tenant:write")),
  labeeb("GET", "/admin/audit", one("audit:read")),
  labeeb("GET", "/admin/audit/{domain}", one("audit:read")),
  labeeb("GET", "/admin/audit/{domain}/{target}", one("audit:read")),

  // ------------------------------------------------------------------- mcp --
  mcp("GET", "/admin/health", { kind: "public" }),
  mcp("GET", "/admin/catalog", one("quota:read")),
  mcp("GET", "/admin/tiers", one("tenant:read")),
  mcp("GET", "/admin/tenants", one("tenant:read")),
  mcp("GET", "/admin/tenant/{tenant_id}", one("tenant:read")),
  mcp("POST", "/admin/tenant/{tenant_id}", {
    kind: "body",
    fields: { tier: "tenant:write", blocked: "block:write" },
  }),
  mcp("GET", "/admin/tenant/{tenant_id}/history", one("tenant:read")),
  mcp("GET", "/admin/tenant/{tenant_id}/usage", one("tenant:read")),
  mcp("GET", "/admin/tenant/{tenant_id}/user/{user_id}", one("tenant:read")),
  mcp("POST", "/admin/tenant/{tenant_id}/user/{user_id}", one("block:write")),
  mcp("GET", "/admin/quota", one("quota:read")),
  mcp("GET", "/admin/quota/{tier}", one("quota:read")),
  mcp("PATCH", "/admin/quota/{tier}", { kind: "single", authority: "quota:write", force: "quota:force" }),
  mcp("DELETE", "/admin/quota/{tier}/{tool}", one("quota:write")),
  mcp("GET", "/admin/settings", one("settings:read")),
  mcp("PATCH", "/admin/settings", one("settings:write")),
  // Retired: MCP answers 404. Rate limits are part of each tier's document,
  // written with PATCH /admin/quota/{tier}. The heading stays in mcp.md until
  // the gateway route and it are removed together.
  mcp("PATCH", "/admin/ratelimit/{lane}", { kind: "retired" }),
  mcp("POST", "/admin/settings/apply", one("service:restart")),
  mcp("GET", "/admin/audit", one("audit:read")),
  mcp("GET", "/admin/audit/{domain}", one("audit:read")),
  mcp("GET", "/admin/audit/{domain}/{target}", one("audit:read")),
  mcp("GET", "/admin/metrics/{metric}", one("fleet:read")),
  mcp("GET", "/admin/mcp-clients", one("settings:read")),
  mcp("POST", "/admin/mcp-clients", one("settings:write")),
  mcp("DELETE", "/admin/mcp-clients", one("settings:write")),
  mcp("GET", "/admin/mcp-clients/requested", one("settings:read")),
  // Paused tools: the whole list, replaced on each POST. The console treats a
  // pause as L3 (it cuts every merchant off those tools); the route is one
  // authority either way.
  mcp("GET", "/admin/availability", one("settings:read")),
  mcp("POST", "/admin/availability", one("settings:write")),
  // Blocked IPs: the WAF refuses one client IP or small range on every path,
  // sign-in included. POST adds one entry and DELETE removes one, both with a
  // `{cidr, reason}` body. The console treats a block as L3.
  mcp("GET", "/admin/waf/blocked-ips", one("settings:read")),
  mcp("POST", "/admin/waf/blocked-ips", one("settings:write")),
  mcp("DELETE", "/admin/waf/blocked-ips", one("settings:write")),
];

/**
 * A catalog path as a pattern. A parameter is one segment, except an audit
 * `{target}`, which may hold slashes (`free/search_orders`,
 * `tenant/<id>/user/<user id>`) — the gateway mounts both products' audit
 * target greedily for exactly that reason.
 */
function patternOf(path: string): RegExp {
  const source = path
    .split("/")
    .map((segment) => {
      if (segment === "{target}") return ".+";
      if (/^\{[^}]+\}$/.test(segment)) return "[^/]+";
      return segment.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    })
    .join("/");
  return new RegExp(`^${source}$`);
}

const PATTERNS = ROUTE_CATALOG.map((r) => ({ route: r, re: patternOf(r.path) }));

/** The catalog route a request matches, or null. `path` carries no query string. */
export function matchRoute(method: string, path: string): CatalogRoute | null {
  const m = method.toUpperCase();
  return PATTERNS.find((p) => p.route.method === m && p.re.test(path))?.route ?? null;
}

/**
 * Whether a holder of these authorities may call a route.
 *
 * `partly` is MCP's two-action `POST /admin/tenant/{id}`: each body field
 * needs its own authority, and holding one lets you send that field alone.
 * A forced call (`?force=true`) needing a second authority is NOT partly —
 * the route itself is callable; `forceNeeds` names the extra one.
 */
export type RouteVerdict = "can" | "cannot" | "partly" | "retired";

export function routeVerdict(
  route: CatalogRoute,
  has: (authority: Authority) => boolean,
): RouteVerdict {
  const a = route.authority;
  switch (a.kind) {
    case "public":
      return "can";
    case "retired":
      return "retired";
    case "single":
      return has(a.authority) ? "can" : "cannot";
    case "body": {
      const held = Object.values(a.fields).filter(has).length;
      if (held === 0) return "cannot";
      return held === Object.keys(a.fields).length ? "can" : "partly";
    }
  }
}

/** Every authority a route can require, in the order the doc names them. */
export function authoritiesOf(route: CatalogRoute): Authority[] {
  const a = route.authority;
  if (a.kind === "single") return a.force ? [a.authority, a.force] : [a.authority];
  if (a.kind === "body") return [...new Set(Object.values(a.fields))];
  return [];
}

/** The catalog's routes for one product, in the documents' order. */
export function routesFor(product: ProductId): CatalogRoute[] {
  return ROUTE_CATALOG.filter((r) => r.product === product);
}
